import React, { useState, useEffect } from 'react';
import { 
  Users, Briefcase, Search, Trash2, Edit2, 
  X, Building2, AlertTriangle, Plus, Check, Pencil
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SegmentedControl from '../ui/SegmentedControl';
import StaffDrawer from './StaffDrawer';
import RankGradeFields from './RankGradeFields';
import { rankOf } from '../../services/ranks';
import { apiFetch } from '../../services/apiClient';
import { INSTITUTIONS, institutionOf, isInstitutionOnly, rotationBadge } from '../../services/staffFields';
import { NO_TEAM_LABEL, teamsOf, teamOf } from '../../services/teams';
import { toast } from '../Toast';
import { fetchPositions } from '../../services/positions';
import PageHeader from '../ui/PageHeader';
import AddNewButton from '../ui/AddNewButton';
import { ControlBar, ToggleGroup, SearchBox } from '../ui/ControlBar';

// --- SUB-COMPONENT: Searchable Selection List ---
const SearchableList = ({ label, items, selected, onToggle, icon: Icon, placeholder, height = "h-32" }) => {
  const [search, setSearch] = useState('');
  const filtered = items.filter(i => i.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Icon className="w-3 h-3 text-slate-400" />
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">{label}</label>
        </div>
        <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{selected.length} Selected</span>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm focus-within:ring-2 focus-within:ring-[var(--primary)]/20 focus-within:border-[var(--primary)] transition-all">
        <div className="flex items-center px-3 py-2 border-b border-slate-100 bg-slate-50/50">
          <Search className="w-3.5 h-3.5 text-slate-400 mr-2" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={placeholder} className="bg-transparent border-none outline-none text-xs w-full font-medium text-slate-600 placeholder:text-slate-300" />
        </div>
        <div className={`${height} overflow-y-auto custom-scrollbar p-1.5 space-y-0.5`}>
          {filtered.length > 0 ? filtered.map(item => {
            const isSelected = selected.includes(item);
            return (
              <button key={item} onClick={() => onToggle(item)} className={`w-full text-left px-3 py-2 rounded-lg text-[11px] font-bold transition-all flex items-center justify-between group ${isSelected ? 'bg-[#042f2e] text-white shadow-md' : 'hover:bg-slate-50 text-slate-600'}`}>
                <span>{item.replace(/^skill_/i, '')}</span>
                {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-slate-300" />}
              </button>
            );
          }) : <div className="py-8 text-center text-[10px] text-slate-300 font-bold uppercase tracking-widest">No matches</div>}
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTS: team management, inline in the Team view ---
// Teams are created, renamed and removed where they are shown, rather than in a
// separate dialog. Each change goes to /api/teams and then refreshes; a rename
// edits the label only, leaving the key -- and so every member -- where it was.
// A team does nothing by itself: the department's rules say what teams mean.
const teamCall = async (path, init, success, onChanged) => {
  try {
    const res = await apiFetch(path, { headers: { 'Content-Type': 'application/json' }, ...init });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || 'That change could not be saved.'); return false; }
    toast.success(success);
    onChanged && onChanged();
    return true;
  } catch {
    toast.error('Request failed — is the backend running?');
    return false;
  }
};

const TeamHeader = ({ team: group, count, onChanged }) => {
  const team = group.label;
  const path = `/api/teams/${encodeURIComponent(group.key)}`;
  const [mode, setMode] = useState('view');            // 'view' | 'rename' | 'delete'
  const [name, setName] = useState(team);
  const [busy, setBusy] = useState(false);
  const rename = async () => {
    const next = name.trim();
    if (!next || next === team) { setMode('view'); return; }
    setBusy(true);
    if (await teamCall(path, { method: 'PUT', body: JSON.stringify({ label: next, members: group.members }) },
      `Renamed to "${next}".`, onChanged)) setMode('view');
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    await teamCall(path, { method: 'DELETE' }, `Team "${team}" removed.`, onChanged);
    setBusy(false);
  };
  return (
    <div className="col-span-full flex items-center gap-3 pt-8 pb-2 border-b border-slate-100 mb-2 first:pt-0 min-h-[44px]">
      {mode === 'rename' ? (
        <>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setMode('view'); }}
            className="h-8 px-2 text-xs font-bold border border-slate-200 rounded-lg outline-none focus:border-[var(--primary)]" />
          <Button size="xs" variant="primary" icon={Check} disabled={busy || !name.trim()} onClick={rename}>Save</Button>
          <Button size="xs" variant="secondary" onClick={() => { setName(team); setMode('view'); }}>Cancel</Button>
        </>
      ) : mode === 'delete' ? (
        <>
          <span className="text-[11px] font-bold text-rose-700">
            Remove "{team}"? {count} {count === 1 ? 'person' : 'people'} will have no team.
          </span>
          <Button size="xs" variant="danger" disabled={busy} onClick={remove}>Remove</Button>
          <Button size="xs" variant="secondary" onClick={() => setMode('view')}>Cancel</Button>
        </>
      ) : (
        <>
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">{team}</span>
          <span className="text-[10px] font-bold text-slate-300">{count}</span>
          <div className="flex gap-1 ml-auto">
            <Button size="xs" variant="ghost" icon={Pencil} title={`Rename ${team}`} onClick={() => { setName(team); setMode('rename'); }} />
            <Button size="xs" variant="ghost" icon={Trash2} title={`Remove ${team}`} onClick={() => setMode('delete')}
              className="hover:bg-[var(--primary)]/10 hover:text-[var(--primary)]" />
          </div>
        </>
      )}
    </div>
  );
};

const NewTeamRow = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const next = name.trim();
    if (!next) return;
    setBusy(true);
    if (await teamCall('/api/teams', { method: 'POST', body: JSON.stringify({ label: next }) },
      `Team "${next}" added.`, onChanged)) { setName(''); setOpen(false); }
    setBusy(false);
  };
  return open ? (
    <div className="flex items-center gap-2 h-12">
      <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="New team name"
        onKeyDown={(e) => { if (e.key === 'Enter') add(); if (e.key === 'Escape') setOpen(false); }}
        className="h-9 w-[240px] px-3 text-xs font-bold border border-slate-200 rounded-xl outline-none focus:border-[var(--primary)]" />
      <Button size="sm" variant="primary" icon={Check} disabled={busy || !name.trim()} onClick={add}>Add team</Button>
      <Button size="sm" variant="secondary" onClick={() => { setName(''); setOpen(false); }}>Cancel</Button>
    </div>
  ) : (
    <AddNewButton label="New team" onClick={() => setOpen(true)} />
  );
};

// Teams a rule in the constraints file names that do not exist yet (GET
// /api/teams lists them): until created they have nobody in them, and the rule
// quietly does nothing for them. Offered here, where teams are managed.
const MissingTeams = ({ onChanged }) => {
  const [missing, setMissing] = useState([]);
  useEffect(() => {
    let live = true;
    apiFetch('/api/teams').then(r => r.json()).then(d => { if (live) setMissing(d?.missing || []); }).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!missing.length) return null;
  const create = async (key) => {
    if (await teamCall(`/api/teams/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify({ label: key, members: [] }) }, `Team "${key}" created.`, onChanged)) {
      setMissing(m => m.filter(k => k !== key));
    }
  };
  return (
    <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-800 font-medium space-y-2">
      <div>The department's rules refer to {missing.length === 1 ? 'a team that does' : 'teams that do'} not exist yet, so nobody is in {missing.length === 1 ? 'it' : 'them'}:</div>
      <div className="flex flex-wrap gap-2">
        {missing.map(k => (
          <button key={k} type="button" onClick={() => create(k)}
            className="flex items-center gap-1 h-7 px-2 rounded-lg bg-white border border-amber-300 font-bold hover:bg-amber-100">
            <Plus className="w-3 h-3" /> Create <code>{k}</code>
          </button>
        ))}
      </div>
    </div>
  );
};

const StaffTab = ({ schedule, onAddEmployee, onRemoveEmployee, onUpdateEmployee, allSkills, loadDemoData }) => {
  // Teams (services/teams.js); no team shows as Unassigned.
  const teams = teamsOf(schedule);
  const todayIso = new Date().toLocaleDateString('en-CA');
  // Rank (default) or Team grouping, for every department; remembered per
  // browser, purely a viewing convenience.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('staffViewMode') === 'team' ? 'team' : 'rank'; } catch { return 'rank'; }
  });
  const chooseView = (v) => { setViewMode(v); try { localStorage.setItem('staffViewMode', v); } catch { /* private window */ } };
  const byTeam = viewMode === 'team';
  const [searchQuery, setSearchQuery] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  // Rank and on-call grade are numbers set on each person (services/ranks.js);
  // the department names them in Ranks & Positions. A title decides nothing.
  const [rankLabels, setRankLabels] = useState({});     // "0".."4" -> display name
  const [gradeLabels, setGradeLabels] = useState({});   // "0".."2" -> display name

  useEffect(() => {
    apiFetch('/api/rank-maps')
      .then(r => r.json())
      .then(d => {
        setRankLabels(d.clinicalRankLabels || d.clinical_rank_labels || {});
        setGradeLabels(d.oncallRankLabels || d.oncall_rank_labels || {});
      })
      .catch(() => {});
  }, []);

  // Display name for a rank number (custom label, else the bare number).
  const rankName = (n) => (rankLabels[String(n)] || '').trim() || `Rank ${n}`;
  const [drawer, setDrawer] = useState({ isOpen: false, employee: null });
  const [removeModal, setRemoveModal] = useState({ isOpen: false, name: '', id: null });
  const [isProcessing, setIsProcessing] = useState(false);

  // Add Modal State
  const [name, setName] = useState('');
  const [addPosition, setAddPosition] = useState('');   // a position key
  const [positions, setPositions] = useState([]);
  useEffect(() => {
    if (addModalOpen) fetchPositions().then(setPositions).catch(() => {});
  }, [addModalOpen]);
  const [addRank, setAddRank] = useState(4);
  const [addGrade, setAddGrade] = useState(null);
  const [addInstitution, setAddInstitution] = useState('');
  const [addTeamName, setAddTeamName] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isFloater, setIsFloater] = useState(false);
  const [isLocum, setIsLocum] = useState(false);
  const [isOnCallEligible, setIsOnCallEligible] = useState(true);

  const openAdd = () => {
    setName(''); setAddPosition(''); setAddRank(4); setAddGrade(null); setAddInstitution(''); setAddTeamName(''); setSelectedSkills([]); setIsFloater(false); setIsLocum(false); setIsOnCallEligible(true);
    setAddModalOpen(true);
  };

  const handleAddSave = async () => {
    if (!name.trim()) return;
    setIsProcessing(true);
    const empData = { 
 name,
      position: addPosition || null,
      rank: addRank,
      oncall_grade: addGrade === null ? '' : addGrade,
      institution: addInstitution || null,
      team: addTeamName || null,          // a team's key
      skills: selectedSkills, 
      is_floater: isFloater, 
      is_locum: isLocum, 
      is_on_call_eligible: isOnCallEligible 
    };
    await onAddEmployee(empData);
    setIsProcessing(false);
    setAddModalOpen(false);
  };

  const handleRemove = async () => {
    setIsProcessing(true);
    await onRemoveEmployee(removeModal.id);
    setIsProcessing(false);
    setRemoveModal({ isOpen: false, name: '', id: null });
  };

  // Sort by rank (0 = most senior), then name. Uses the unified rank system.
  const filteredEmployees = (schedule?.employees || [])
    .filter(e => e.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const rankA = rankOf(a);
      const rankB = rankOf(b);
      if (rankA !== rankB) return rankA - rankB;   // lower rank number first
      return a.name.localeCompare(b.name);
    });

  // Groups for the grid: one per rank (labelled by the custom rank name), or in
  // the Team view one per team in the library's order -- empty teams included,
  // so a new team can be seen and managed -- then everyone without a team.
  const groups = [];
  if (byTeam) {
    teams.forEach(t => groups.push({ id: `team-${t.key}`, team: t,
      emps: filteredEmployees.filter(e => teamOf(teams, e)?.key === t.key) }));
    const none = filteredEmployees.filter(e => !teamOf(teams, e));
    if (none.length) groups.push({ id: 'team-none', label: NO_TEAM_LABEL, emps: none });
  } else {
    filteredEmployees.forEach(emp => {
      const r = rankOf(emp);
      const last = groups[groups.length - 1];
      if (!last || last.rank !== r) groups.push({ id: `rank-${r}`, rank: r, label: rankName(r), emps: [] });
      groups[groups.length - 1].emps.push(emp);
    });
  }

  const refresh = () => loadDemoData && loadDemoData('SMALL');

  const renderCard = (emp) => {
          const institution = institutionOf(emp);
          const team = teamOf(teams, emp)?.label;
          const rotation = rotationBadge(emp, todayIso);
          const isEligible = emp.is_on_call_eligible !== undefined ? emp.is_on_call_eligible : (emp.isOnCallEligible !== undefined ? emp.isOnCallEligible : true);
          return (
            <Card key={emp.name} padding="p-0" className="flex flex-col group overflow-hidden animate-in fade-in zoom-in-95 duration-300">
              <div className="p-5 flex-1 relative">
                <div className="flex justify-between items-start mb-5">
                  <div className="min-w-0">
                    <h4 className="font-bold text-slate-900 tracking-tight truncate">{emp.name}</h4>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {getRankBadge(emp)}
                      {institution && <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest bg-slate-100 text-slate-500" title={isInstitutionOnly(emp) ? `Only works at ${institution} locations` : undefined}>{institution}{isInstitutionOnly(emp) ? ' only' : ''}</span>}
                      {team && <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest bg-teal-50 text-teal-700" title="Team">{team}</span>}
                      {rotation && (
                        <span title="Rotation dates — not rostered outside them"
                          className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest ${rotation.tone === 'off' ? 'bg-amber-100 text-amber-800' : 'bg-slate-50 text-slate-400'}`}>
                          {rotation.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="xs" onClick={() => setDrawer({ isOpen: true, employee: emp })} icon={Edit2} />
                    <Button variant="ghost" size="xs" onClick={() => setRemoveModal({ isOpen: true, name: emp.name, id: emp.id })} icon={Trash2} className="hover:bg-[var(--primary)]/10 hover:text-[var(--primary)]" />
                  </div>
                </div>
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center gap-2 mb-2.5 ml-1"><Briefcase className="w-3 h-3 text-slate-300" /><span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Qualifications</span></div>
                    <div className="flex flex-wrap gap-1.5">
                      {(emp.skills || []).sort().map(skill => <span key={skill} className="px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[9px] font-bold text-slate-600 shadow-sm">{skill.replace(/^skill_/i, '')}</span>)}
                      {(!emp.skills || emp.skills.length === 0) && <span className="text-[9px] text-slate-300 italic">No skills listed</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-50 bg-slate-50/30 rounded-b-[20px] flex items-center justify-between">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  {(emp.isLocum || emp.is_locum) ? 'Locum' : ((emp.isFloater || emp.is_floater) ? 'Floater' : 'Core Staff')}
                </span>
                {!isEligible && <span className="text-[9px] font-bold text-rose-400 uppercase tracking-widest">No-Call</span>}
              </div>
            </Card>
          );
  };

  // Badge shows a short form of the custom rank name, tone by seniority.
  const rankTone = (r) => (
    r === 0 ? 'bg-[#042f2e] text-white'
    : r === 1 ? 'bg-[var(--primary)] text-white'
    : r === 2 ? 'bg-slate-100 text-slate-600'
    : 'bg-slate-50 text-slate-400 border border-slate-200'
  );
  const getRankBadge = (emp) => {
    const r = rankOf(emp);
    const name = rankName(r);
    // short code: initials of the name, or the number if unnamed
    const short = (rankLabels[String(r)] || '').trim()
      ? name.split(/\s+/).map(w => w[0]).join('').slice(0, 3).toUpperCase()
      : String(r);
    return <span title={name} className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${rankTone(r)}`}>{short}</span>;
  };

  return (
    <div className="animate-in fade-in space-y-6">
      {/* ADD MODAL */}
      {addModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">Add New Staff</h3>
                <button onClick={() => setAddModalOpen(false)} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-6 mb-10">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Full Name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dr. Lee" className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all shadow-sm" />
                  </div>
                </div>
                <RankGradeFields position={addPosition} setPosition={setAddPosition} positions={positions} rank={addRank} setRank={setAddRank}
                  grade={addGrade} setGrade={setAddGrade} rankLabels={rankLabels} gradeLabels={gradeLabels} />
                
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1 flex items-center gap-2"><Building2 className="w-3 h-3" /> Institution</label>
                  <SegmentedControl value={addInstitution} onChange={setAddInstitution} options={INSTITUTIONS.map(i => ({ id: i, label: i }))} className="h-[44px]" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Team</label>
                  <select value={addTeamName} onChange={(e) => setAddTeamName(e.target.value)}
                    className="w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[var(--primary)] cursor-pointer">
                    <option value="">{NO_TEAM_LABEL}</option>
                    {teams.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1 flex items-center gap-2"><Briefcase className="w-3 h-3" /> Staff Role</label>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Floater</span>
                      <button onClick={() => setIsFloater(!isFloater)} className={`w-8 h-4.5 rounded-full relative transition-all ${isFloater ? 'bg-emerald-500' : 'bg-slate-300'}`} style={{ minWidth: '32px', height: '18px' }}><div className={`absolute w-3 h-3 bg-white rounded-full transition-all top-0.5 ${isFloater ? 'left-[16px]' : 'left-0.5'}`} /></button>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-l border-slate-200 pl-4">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Locum</span>
                      <button onClick={() => setIsLocum(!isLocum)} className={`w-8 h-4.5 rounded-full relative transition-all ${isLocum ? 'bg-emerald-500' : 'bg-slate-300'}`} style={{ minWidth: '32px', height: '18px' }}><div className={`absolute w-3 h-3 bg-white rounded-full transition-all top-0.5 ${isLocum ? 'left-[16px]' : 'left-0.5'}`} /></button>
                    </div>
                  </div>
                </div>

                <SearchableList 
                  label="Initial Qualifications" 
                  items={allSkills} 
                  selected={selectedSkills} 
                  onToggle={(s) => setSelectedSkills(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])} 
                  icon={Briefcase} 
                  placeholder="Search skills library..." 
                  height="h-32" 
                />
              </div>
              <div className="flex gap-4">
                <Button variant="secondary" onClick={() => setAddModalOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleAddSave} disabled={isProcessing || !name.trim()} isLoading={isProcessing} className="flex-1" icon={Plus} size="lg">Add Staff Member</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <StaffDrawer 
        isOpen={drawer.isOpen}
        onClose={() => setDrawer({ isOpen: false, employee: null })}
        employee={drawer.employee}
        allSkills={allSkills}
        onUpdateEmployee={onUpdateEmployee}
        teams={teams}
        staff={schedule?.employees || []}
      />

      {removeModal.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-[var(--primary)]/10 text-[var(--primary)] rounded-2xl flex items-center justify-center mx-auto mb-6"><AlertTriangle className="w-8 h-8" /></div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Remove Staff Member</h3>
              <p className="text-sm text-slate-500 font-medium mb-8">Are you sure you want to remove <span className="font-bold text-slate-900">{removeModal.name}</span>? This action cannot be undone.</p>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setRemoveModal({ isOpen: false, name: '', id: null })} className="flex-1">Cancel</Button>
                <Button variant="primary" onClick={handleRemove} isLoading={isProcessing} className="flex-1">Remove</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FLOATING IDENTITY */}
      <PageHeader icon={Users} title="Staff List" />

      <ControlBar>
        <ToggleGroup label="View" value={viewMode} onChange={chooseView}
          options={[{ id: 'rank', label: 'Rank' }, { id: 'team', label: 'Team' }]} />
        <SearchBox value={searchQuery} onChange={setSearchQuery} placeholder="Search staff..." />
      </ControlBar>

      {byTeam && <MissingTeams onChanged={refresh} />}
      {groups.map(group => (
        <div key={group.id} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {group.team !== undefined
            ? <TeamHeader team={group.team} count={group.emps.length} onChanged={refresh} />
            : (
              <div className="col-span-full pt-8 pb-2 border-b border-slate-100 mb-2 first:pt-0">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">{group.label}</span>
                {byTeam && <span className="ml-3 text-[10px] font-bold text-slate-300">{group.emps.length}</span>}
              </div>
            )}
          {group.emps.map(renderCard)}
          {group.team !== undefined && group.emps.length === 0 && (
            <p className="col-span-full text-xs text-slate-300 italic ml-1">Nobody in this team yet — set it from a person's edit drawer.</p>
          )}
        </div>
      ))}
      {/* Adding, at the bottom of the list like everywhere else (Oct 2026). */}
      <div className={`mt-8 grid gap-4 ${byTeam ? 'md:grid-cols-2' : ''}`}>
        <AddNewButton label="New staff" onClick={openAdd} />
        {byTeam && <NewTeamRow onChanged={refresh} />}
      </div>
    </div>
  );
};

export default StaffTab;
